import { defineStore } from 'pinia'
import axios from 'axios'
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency'
import { Capacitor } from '@capacitor/core'
import { FirebaseAnalytics } from '@capacitor-firebase/analytics'
import { gaDimensionsFromStoredUtm } from '@/utils/utm'
import { analyticsPagePath } from '@/utils/analyticsPath'

const STORAGE_KEY = 'yumnia_consent'
const CONSENT_LOG_KEY = 'yumnia_consent_log'
const CONSENT_VERSION = '1.0.0' // Version of the consent implementation

export const useConsentStore = defineStore('consent', {
  state: () => ({
    loaded: false,
    showBanner: false,
    attStatus: 'unknown',
    consent: {
      essential: true,
      analytics: false,
      marketing: false,
    },
    consentLogs: [], // Store consent logs for auditability
  }),

  actions: {
    async init () {
      const platform = Capacitor.getPlatform()

      // On native, immediately disable analytics collection until consent is confirmed.
      // The native Firebase SDK auto-starts on app launch; this prevents unconsented collection.
      if (Capacitor.isNativePlatform()) {
        await this._syncNativeAnalyticsEnabled(false)
      }

      // Load consent settings
      const existing = localStorage.getItem(STORAGE_KEY)
      if (existing) {
        this.consent = JSON.parse(existing)
        this.showBanner = false
        this.loaded = true
        this._updateGtagConsent(this.consent)
        this._emitConsentEvent('consent_state_ready', {
          consentAction: 'restored',
          consentSource: 'startup_restore',
        })
        await this._syncNativeAnalyticsEnabled()

        // Load consent logs if they exist
        const existingLogs = localStorage.getItem(CONSENT_LOG_KEY)
        if (existingLogs) {
          this.consentLogs = JSON.parse(existingLogs)
        }

        // On iOS, ensure ATT is synced even if we have local storage
        if (platform === 'ios') {
          await this._handleIOSATT()
        }

        return
      }

      // Special handling for iOS - Use Native ATT instead of custom banner
      if (platform === 'ios') {
        this.loaded = true
        this.showBanner = false
        await this._handleIOSATT()
        return
      }

      try {
        const { data } = await axios.get('/api/consent/status')
        this.showBanner = data.show_banner

        // If banner should not be shown, automatically accept all cookies
        if (!data.show_banner) {
          await this.acceptAll('banner')
        }
      } catch (e) {
        console.error('Consent status check failed', e)

        // Assume a consent jurisdiction. This used to accept everything on the
        // reader's behalf, so an EU visitor whose check failed had analytics and
        // marketing switched on without ever being asked. Failing towards the
        // banner costs some analytics coverage; failing the other way is a
        // breach, and most of our readers are in the EU.
        this.showBanner = true
      } finally {
        this.loaded = true
      }
    },

    async _handleIOSATT () {
      try {
        const { status } = await AppTrackingTransparency.getStatus()
        this.attStatus = status
        if (status === 'notDetermined') {
          const result = await AppTrackingTransparency.requestPermission()
          this.attStatus = result.status
          if (result.status === 'authorized') {
            await this.acceptAll('att')
          } else {
            await this.rejectAll('att')
          }
        } else if (status === 'authorized') {
          // If already authorized, ensure our local state matches
          if (!this.consent.analytics || !this.consent.marketing) {
            await this.acceptAll('att')
          }
        } else {
          // If denied/restricted, ensure our local state matches
          if (this.consent.analytics || this.consent.marketing) {
            await this.rejectAll('att')
          }
        }
      } catch (e) {
        console.error('ATT failed', e)
        // Fallback to essential only if ATT fails and we don't have existing consent
        if (!localStorage.getItem(STORAGE_KEY)) {
          await this.rejectAll('att')
        }
      }
    },

    async _save (consentSource = 'banner') {
      // Save consent settings
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.consent))
      this.showBanner = false

      // Log consent change with timestamp and version
      const logEntry = {
        timestamp: new Date().toISOString(), version: CONSENT_VERSION, consent: { ...this.consent }, // Create a copy of the consent object
        action: this._determineAction()
      }

      // Add to logs
      this.consentLogs.push(logEntry)

      // Save logs to localStorage
      localStorage.setItem(CONSENT_LOG_KEY, JSON.stringify(this.consentLogs))
      const action = this._determineAction()

      // Update consent signals for GTM
      this._updateGtagConsent(this.consent)
      this._emitConsentEvent('cookie_consent_update', {
        consentAction: action,
        consentSource,
      })
      // Sync native Firebase Analytics collection state
      await this._syncNativeAnalyticsEnabled()

      // Store consent on the server
      try {
        // Prepare consent data
        const consentData = {
          consent_version: CONSENT_VERSION, consent_source: consentSource, consent_choices: {
            analytics: this.consent.analytics, marketing: this.consent.marketing
          }, // Include session ID if available
          session_id: localStorage.getItem('session_id') || null
        }

        // Send consent data to the server
        await axios.post('/api/consent', consentData)
      } catch (error) {
        console.error('Failed to store consent on server:', error)
        // Continue even if server storage fails - local storage is still maintained
      }
    },

    _determineAction () {
      // Determine what action the user took (accept all, reject non-essential, or custom)
      if (this.consent.analytics && this.consent.marketing) {
        return 'accept_all'
      } else if (!this.consent.analytics && !this.consent.marketing) {
        return 'essential_only'
      } else {
        return 'custom'
      }
    },

    _updateGtagConsent (consent) {
      const analyticsStorage = consent.analytics ? 'granted' : 'denied'
      const adStorage = consent.marketing ? 'granted' : 'denied'
      gtag('consent', 'update', {
        analytics_storage: analyticsStorage,
        ad_storage: adStorage,
        ad_user_data: adStorage,
        ad_personalization: adStorage,
      })
    },

    _eventPlatform () {
      return Capacitor.isNativePlatform() ? 'native' : 'web'
    },

    _emitConsentEvent (eventName, { consentAction, consentSource }) {
      if (!window.dataLayer || !Array.isArray(window.dataLayer)) return
      window.dataLayer.push({
        event: eventName,
        analytics_granted: !!this.consent.analytics,
        marketing_granted: !!this.consent.marketing,
        consent_action: consentAction,
        consent_source: consentSource,
        app_platform: this._eventPlatform(),
        consent_version: CONSENT_VERSION,
      })
    },

    async acceptAll (consentSource = 'banner') {
      this.consent = { essential: true, analytics: true, marketing: true }
      await this._save(consentSource)
    },

    async rejectAll (consentSource = 'banner') {
      this.consent = { essential: true, analytics: false, marketing: false }
      await this._save(consentSource)
    },

    async saveCustom (prefs, consentSource = 'modal') {
      this.consent = { essential: true, ...prefs }
      await this._save(consentSource)
    },

    async _syncNativeAnalyticsEnabled (enabled = this.consent.analytics) {
      if (!Capacitor.isNativePlatform()) return
      try {
        await FirebaseAnalytics.setEnabled({ enabled: !!enabled })
      } catch (e) {
        console.error('Failed to sync native analytics state:', e)
      }
    },

    async trackEvent (eventName, params = {}) {
      if (!this.consent.analytics) return
      const payload = params && typeof params === 'object' ? { ...params } : {}

      if (Capacitor.isNativePlatform()) {
        await FirebaseAnalytics.logEvent({ name: eventName, params: payload })
        window.dataLayer.push({ event: eventName, ...payload, app_platform: 'native' })
      } else {
        window.dataLayer.push({ event: eventName, ...payload, app_platform: 'web' })
      }
    },

    async trackPageView (to) {
      if (!this.consent.analytics) return
      const params = { page_path: analyticsPagePath(to.fullPath), page_title: to.meta?.title || document.title }

      if (Capacitor.isNativePlatform()) {
        await FirebaseAnalytics.setCurrentScreen({ screenName: to.name || params.page_path })
        await FirebaseAnalytics.logEvent({ name: 'page_view', params })
        window.dataLayer.push({ event: 'page_view', ...params, app_platform: 'native' })
      } else {
        window.dataLayer.push({ event: 'page_view', ...params, app_platform: 'web' })
      }
    },

    async trackLogin ({ method = 'email' } = {}) {
      await this.trackEvent('login', { method, ...gaDimensionsFromStoredUtm() })
    },

    async trackRegistration ({ method = 'email', signup_source: signupSource } = {}) {
      const payload = { method, ...gaDimensionsFromStoredUtm() }
      if (signupSource) payload.signup_source = signupSource
      await this.trackEvent('sign_up', payload)
    },
  },
})
